/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
// Formattazione durate in giorni / ore / minuti.
// Il backend restituisce le ore come numero decimale (es. 1.53 = 1h 32m):
// qui le convertiamo in un formato leggibile invece di mostrare il decimale grezzo.
// I secondi non vengono mai mostrati: si arrotonda sempre al minuto successivo.

const MINUTO = 60;
const ORA = 3600;
const GIORNO = 86400;

// Ore decimali (numero o stringa) -> secondi interi
export function oreToSecondi(ore) {
  const n = typeof ore === 'number' ? ore : parseFloat(ore);
  if (!isFinite(n) || n <= 0) return 0;
  return Math.round(n * ORA);
}

// Secondi -> "2g 3h 15m" (arrotonda al minuto successivo, omette le unita' iniziali a zero)
// opzioni: { compatto: true } per mostrare al massimo 2 unita'
export function fmtDurata(sec, { compatto = false } = {}) {
  const minutiTot = Math.max(0, Math.ceil((Number(sec) || 0) / MINUTO));
  const tot = minutiTot * MINUTO;

  const g = Math.floor(tot / GIORNO);
  const h = Math.floor((tot % GIORNO) / ORA);
  const m = Math.floor((tot % ORA) / MINUTO);

  const parti = [];
  if (g) parti.push([g, 'g']);
  if (h || parti.length) parti.push([h, 'h']);
  parti.push([m, 'm']);

  // toglie le unita' finali a zero ("2h 0m" -> "2h"), lasciandone sempre almeno una
  while (parti.length > 1 && parti[parti.length - 1][0] === 0) parti.pop();

  const testo = parti.map(([v, u]) => `${v}${u}`);
  return compatto ? testo.slice(0, 2).join(' ') : testo.join(' ');
}

// Ore decimali -> "2g 3h 15m"
export function fmtDurataOre(ore, opts) {
  return fmtDurata(oreToSecondi(ore), opts);
}

// Secondi effettivi di una sessione di lavoro.
// Se abbiamo inizio/fine E il campo pausa_minuti usiamo i timestamp (precisione al secondo);
// altrimenti ricadiamo sulle ore decimali gia' calcolate dal backend, che a seconda
// dell'endpoint arrivano come ore_calcolate oppure durata_ore.
export function secondiSessione(s) {
  if (!s) return 0;
  const decimali = s.ore_calcolate != null ? s.ore_calcolate : s.durata_ore;
  const haPausa = s.pausa_minuti !== undefined && s.pausa_minuti !== null;
  if (s.inizio && s.fine && (haPausa || decimali == null)) {
    const a = new Date(s.inizio).getTime();
    const b = new Date(s.fine).getTime();
    if (!isNaN(a) && !isNaN(b) && b > a) {
      const pausa = (parseInt(s.pausa_minuti, 10) || 0) * MINUTO;
      return Math.max(0, Math.round((b - a) / 1000) - pausa);
    }
  }
  return oreToSecondi(decimali);
}

// Somma in secondi di piu' sessioni (ignora quelle ancora aperte)
export function secondiTotaliSessioni(sessioni = []) {
  return sessioni.filter((s) => s && s.fine).reduce((acc, s) => acc + secondiSessione(s), 0);
}
